'use client';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/components/ui/tooltip';
import {
  SpringConfig,
  config as _config,
  animated,
  useSpring,
} from '@react-spring/web';
import { isNumber, isString } from 'lodash-es';
import { ComponentProps, ReactNode, useMemo } from 'react';
import { formatUnits } from 'viem';

export interface AnimatedNumberProps
  extends Omit<ComponentProps<'span'>, 'value' | 'prefix' | 'children'> {
  /**
   * 当前值，可以是 number 或者 bignumber，bignumber 会以 18 位小数去转换
   */
  value: number | string | bigint;
  /**
   * useSpring config
   */
  config?: SpringConfig;
  /**
   * 保留小数点位数
   */
  fractionDigits?: number;
  /**
   * 前缀
   */
  prefix?: ReactNode;
  disablePrefix?: boolean;
  /**
   * 后缀
   */
  suffix?: ReactNode;
  disableSuffix?: boolean;
  /**
   * 是否启用变化动画
   */
  animated?: boolean;
  /**
   * 是否启用 tooltip，鼠标 hover 时展示高精度数据。
   */
  tooltip?: boolean;
  /**
   * 当数据为 BigNumber 时，value 的小数点位数，默认参考 ETH 为 18 位
   */
  decimals?: number;
  /**
   * The formatting that should be displayed for the number. Possible values are: "standard" (default), "compact".
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#notation
   */
  notation?: 'standard' | 'compact';
  /**
   * Return the exponent of ten when divisible by three. String representing exponent; defaults to using the "short" form.
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#compactdisplay
   * */
  compactDisplay?: 'short' | 'long';
  /**
   * Intl.NumberFormat options
   */
  options?: Intl.NumberFormatOptions;
}

/**
 * Digital displays with animations and prefixes and suffixes
 */
export function AnimatedNumber({
  value,
  config,
  fractionDigits = 18,
  prefix,
  disablePrefix = false,
  suffix,
  disableSuffix = false,
  animated,
  tooltip,
  decimals = 18,
  notation,
  compactDisplay,
  options,
  ...remainingProps
}: AnimatedNumberProps) {
  const numberFormat = useMemo(
    () =>
      new Intl.NumberFormat(
        'en-US',
        Object.assign(
          {
            // minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
            notation,
            compactDisplay,
          },
          options
        )
      ),
    [fractionDigits, notation, compactDisplay, options]
  );
  const valueAsNumber = useMemo(() => {
    let numValue;
    if (isNumber(value)) {
      numValue = value;
    } else if (isString(value)) {
      const parsed = Number(value);
      numValue = isNaN(parsed) ? 0 : parsed;
    } else {
      numValue = Number(formatUnits(value, decimals));
    }
    return numValue;
  }, [value, decimals, fractionDigits]);
  const children = animated ? (
    <AnimatedNumberImpl
      valueAsNumber={valueAsNumber}
      config={config}
      numberFormat={numberFormat}
      fractionDigits={fractionDigits}
    />
  ) : (
    numberFormat.format(valueAsNumber)
  );
  return (
    <span {...remainingProps}>
      {!disablePrefix && prefix}
      {tooltip ? (
        <TooltipImpl value={value} decimals={decimals}>
          {children}
        </TooltipImpl>
      ) : (
        children
      )}
      {!disableSuffix && suffix}
    </span>
  );
}

interface AnimatedNumberImplProps {
  /**
   * 当前值 number
   */
  valueAsNumber: number;
  /**
   * useSpring config
   */
  config?: SpringConfig;
  numberFormat: Intl.NumberFormat;
  fractionDigits?: number;
}

function AnimatedNumberImpl({
  valueAsNumber,
  config = _config.molasses,
  numberFormat,
  fractionDigits,
}: AnimatedNumberImplProps) {
  const fixedNumberFormat = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }),
    [fractionDigits]
  );
  const props = useSpring({
    value: valueAsNumber,
    config,
  });
  return (
    <animated.span>
      {props.value.to(val =>
        props.value.isAnimating
          ? fixedNumberFormat.format(val)
          : numberFormat.format(valueAsNumber)
      )}
    </animated.span>
  );
}

interface TooltipImplProps {
  value: number | string | bigint;
  decimals: number;
  children: ReactNode;
}
function TooltipImpl({ value, decimals, children }: TooltipImplProps) {
  const numberFormat = useMemo(() => new Intl.NumberFormat(), []);
  const content = useMemo(() => {
    if (isNumber(value)) {
      return numberFormat.format(value);
    } else if (isString(value)) {
      try {
        return numberFormat.format(Number(value));
      } catch (error) {
        return value;
      }
    } else {
      return numberFormat.format(Number(formatUnits(value, decimals)));
    }
  }, [value, decimals, numberFormat]);

  return (
    <Tooltip>
      <TooltipTrigger type="button">{children}</TooltipTrigger>
      <TooltipContent className="font-mono">{content}</TooltipContent>
    </Tooltip>
  );
}
